import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import logo from '../IMAGES/nidologo.png';
import mark from '../IMAGES/mark.png';


/* ================= WRAPPER ================= */

const FooterWrapper = styled.footer`
  margin-top: 0;
  position:relative;

  @media (max-width: 640px) {
    margin-top: 0;
  }
`;

/* ================= TOP VISUAL ================= */

const TopVisual = styled.div`
  position: relative;
  background: #c66f4e url(${mark}) 75% center / cover no-repeat;
  min-height: 520px;
  color: white;
  overflow:hidden;

  @media (max-width: 640px) {
    min-height: 0;
  }
`;


const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(115deg,rgba(171,78,48,.99) 0%,rgba(198,104,71,.96) 48%,rgba(214,125,84,.87) 100%);

  &::after{
    content:'';
    position:absolute;
    width:520px;
    height:520px;
    right:-160px;
    top:-250px;
    border:1px solid rgba(255,255,255,.2);
    border-radius:50%;
    box-shadow:0 0 0 70px rgba(255,255,255,.025),0 0 0 140px rgba(255,255,255,.02);
  }
`;

const TopInner = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1320px;
  margin: 0 auto;
  padding: clamp(75px,8vw,112px) clamp(28px,5vw,64px) 90px;

  display: grid;
  grid-template-columns: 1.45fr .72fr .72fr 1fr;
  gap: clamp(35px,5vw,70px);

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 56px;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    padding: 64px 24px;
    gap: 40px;
  }
`;

/* ================= BRAND ================= */

const Logo = styled(Link)`
  position:relative;
  display:block;
  width:174px;
  height:92px;
  overflow:hidden;
  margin-bottom:26px;
`;

const LogoImg = styled.img`
  position:absolute;
  width:385px;
  max-width:none;
  height:auto;
  left:-107px;
  top:-70px;
  filter:brightness(0) invert(1);

  @media (max-width: 640px) {
    width:350px;
    left:-97px;
    top:-63px;
  }
`;

const BrandText = styled.p`
  font-size: clamp(1.25rem,2vw,1.75rem);
  line-height: 1.28;
  letter-spacing:-.025em;
  max-width: 440px;
  margin:0;
  color:rgba(255,255,255,.94);
`;

/* ================= COLUMNS ================= */

const Column = styled.div``;

const ColumnTitle = styled.h4`
  font-size: 0.75rem;
  margin: 5px 0 24px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.17em;
  color:rgba(255,255,255,.62);
`;

const FooterLink = styled(Link)`
  display: block;
  margin-bottom: 14px;
  font-size: 0.94rem;
  color: white;
  opacity: 0.92;
  text-decoration: none;
  transition:transform .2s ease,opacity .2s ease;

  &:hover {
    opacity: 1;
    text-decoration: none;
    transform:translateX(4px);
  }
`;

/* ================= BOTTOM ================= */

const Bottom = styled.div`
  background: #29474f;
  color: rgba(255,255,255,.68);
  font-size: 0.8rem;
`;

const ConsumerBar = styled.div`
  background:#f7f5ef;
  border-top:1px solid rgba(41,71,79,.12);
  border-bottom:1px solid rgba(41,71,79,.12);
`;

const ConsumerInner = styled.div`
  max-width:1320px;
  margin:auto;
  padding:22px clamp(24px,5vw,64px);
  display:flex;
  flex-wrap:wrap;
  align-items:center;
  gap:11px 25px;
  >span{margin-right:auto;color:#29474f;font-size:.72rem;font-weight:850;letter-spacing:.12em;}
  a{color:#526a70;font-size:.8rem;font-weight:720;text-underline-offset:3px;}
  .contact-cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 9px 16px;
    border-radius: 999px;
    background: #ffffff;
    border: 1px solid rgba(41,71,79,.12);
    color: #29474f;
    box-shadow: 0 4px 10px rgba(41,71,79,.08);
    text-decoration: none;
    font-weight: 800;
  }
  .contact-cta:hover { text-decoration: none; }
  @media(max-width:720px){align-items:flex-start;flex-direction:column;gap:13px;>span{margin:0 0 4px;}}
`;

const BottomInner = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  padding: 26px clamp(24px,5vw,64px);

  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const BottomLinks = styled.div`
  display:flex;
  flex-wrap:wrap;
  gap:14px;
  align-items:center;

  a, button {
    border:0;
    background:transparent;
    padding:0;
    color:rgba(255,255,255,.72);
    font:inherit;
    text-decoration:none;
    cursor:pointer;
  }

  a:hover, button:hover { text-decoration:underline; }

  @media(max-width:640px){ justify-content:center; }
`;

/* ================= COMPONENT ================= */

export default function Footer() {
  const { user } = useAuth();
  return (
    <FooterWrapper>
      <TopVisual>
        <Overlay />

        <TopInner>
          {/* Brand */}
          <div>
            <Logo to="/">
              <LogoImg src={logo} alt="NIDO" />
            </Logo>
            <BrandText>
              Un portal inmobiliario claro, humano y pensado para ayudarte a
              encontrar tu lugar en Mendoza, San Juan y San Luis.
            </BrandText>
          </div>

          {/* Column 1 */}
          <Column>
            <ColumnTitle>Explorar</ColumnTitle>
            <FooterLink to="/propiedades/mendoza">Propiedades en Mendoza</FooterLink>
            <FooterLink to="/propiedades/san-juan">Propiedades en San Juan</FooterLink>
            <FooterLink to="/propiedades/san-luis">Propiedades en San Luis</FooterLink>
            <FooterLink to="/alquileres/mendoza">Alquileres en Mendoza</FooterLink>
            <FooterLink to="/casas-en-venta/mendoza">Casas en venta</FooterLink>
            <FooterLink to="/vacaciones">Vacaciones</FooterLink>
            <FooterLink to="/explorar-cuyo">Explorar el mapa</FooterLink>
            <FooterLink to="/desarrollos">Desarrollos</FooterLink>
          </Column>

          {/* Column 2 */}
          <Column>
            <ColumnTitle>Sobre NIDO</ColumnTitle>
            <FooterLink to="/quienes-somos">Quiénes somos</FooterLink>
            <FooterLink to="/vivir-cuyo">Una manera de vivir</FooterLink>
            <FooterLink to="/legal/seguridad">Centro de seguridad</FooterLink>
            <FooterLink to="/legal/publicaciones">Cómo publicar</FooterLink>
            <FooterLink to="/publicar">Publicar propiedad</FooterLink>
          </Column>

          <Column>
            <ColumnTitle>Legal y confianza</ColumnTitle>
            <FooterLink to="/legal/terminos">Términos y condiciones</FooterLink>
            <FooterLink to="/legal/privacidad">Privacidad</FooterLink>
            <FooterLink to="/legal/cookies">Cookies</FooterLink>
            <FooterLink to="/legal/publicaciones">Reglas de publicación</FooterLink>
            <FooterLink to="/legal/seguridad">Prevención de fraude</FooterLink>
            <FooterLink to="/legal/intelectual">Propiedad intelectual</FooterLink>
            <FooterLink to="/legal/contratacion">Pagos y cancelaciones</FooterLink>
            <FooterLink to="/legal/consumidor">Consumidores y usuarios</FooterLink>
          </Column>
        </TopInner>
      </TopVisual>

      <ConsumerBar>
        <ConsumerInner>
          <span>DEFENSA DEL CONSUMIDOR</span>
          <a className="contact-cta" href="mailto:info@nidoportal.com?subject=Consulta%20NIDO">Contactanos</a>
          <a href="mailto:info@nidoportal.com?subject=Arrepentimiento%20de%20contrataci%C3%B3n%20NIDO">Botón de arrepentimiento</a>
          <Link to={user?'/client/suscripcion':'/login?returnTo=%2Fclient%2Fsuscripcion'}>Botón de baja de servicios</Link>
          <a href="https://www.argentina.gob.ar/servicio/iniciar-un-reclamo-ante-defensa-del-consumidor" target="_blank" rel="noreferrer">Defensa de las y los consumidores</a>
          <Link to="/legal/consumidor">Contratos de adhesión · Ley 24.240</Link>
        </ConsumerInner>
      </ConsumerBar>

      <Bottom>
        <BottomInner>
          <div>
            © {new Date().getFullYear()} NIDO — Cuyo, Argentina
          </div>
          <BottomLinks><Link to="/legal">Centro legal</Link><Link to="/legal/terminos">Términos</Link><Link to="/legal/privacidad">Privacidad</Link><button type="button" onClick={()=>{localStorage.removeItem('nido_privacy_choice_v1');window.location.reload();}}>Preferencias de privacidad</button></BottomLinks>
        </BottomInner>
      </Bottom>
    </FooterWrapper>
  );
}
